import Dropzone from "react-dropzone-uploader-with-credentials";
import '../assets/css/upload.css';
import API from "../api";
import { trackEvent } from "../analytics";
import {useAuth} from "../AuthUser";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {Col, Row, Container, Card, Modal, Button} from "react-bootstrap";
import {useRef, useState} from "react";

const notify = (message) =>
	toast.error(message, {
		position: toast.POSITION.TOP_RIGHT,
	});

// --- Helpers: classify files ---
const VIDEO_MIME_PREFIX = 'video/';
const VIDEO_EXTS = ['.mp4', '.avi', '.mov', '.webm'];
const REPORT_EXTS = ['.pdf', '.xls', '.xlsx', '.csv'];

const isVideoFile = (f) => {
	const name = (f?.meta?.name || f?.name || '').toLowerCase();
	const type = (f?.meta?.type || f?.type || '').toLowerCase();
	return type.startsWith(VIDEO_MIME_PREFIX) || VIDEO_EXTS.some(ext => name.endsWith(ext));
};

const isReportFile = (f) => {
	const name = (f?.meta?.name || f?.name || '').toLowerCase();
	return REPORT_EXTS.some(ext => name.endsWith(ext));
};

// -------------------------------

const Layout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }) => {
	return (
		<div>
			{previews}
			<div {...dropzoneProps}>
				{files.length < maxFiles && input}
				<textarea name="notes"/>
				{files.length > 0 && submitButton}
			</div>
		</div>
	)
}

const Input = ({ accept, onFiles, files, getFilesFromEvent }) => {
	const text = files.length > 0 ? 'Add more files' : 'Choose files'
	return (
		<div className="input-group">
			<input
				type="file"
				className="form-control"
				id="inputGroupFile04"
				aria-describedby="inputGroupFileAddon04"
				aria-label="Upload"
				style={{ display: 'none' }}
				accept={accept}
				multiple
				onChange={e => {
					getFilesFromEvent(e).then(chosenFiles => {
						onFiles(chosenFiles)
					})
				}}>
				<button className="btn btn-outline-secondary" type="button" id="inputGroupFileAddon04">{text}</button>
			</input>
		</div>
	)
}

const NoDropzoneLayout = ({ previews, submitButton, input, files, dropzoneProps, extra: { maxFiles } }) => {
	let { user } = useAuth();
	const { ref, className, style } = dropzoneProps;

	const videosSelected = files.filter(f => isVideoFile(f)).length;
	const reportsSelected = files.filter(f => isReportFile(f)).length;

	return (
		<>
			<Container>
				{/* Desktop (lg+): side-by-side; Mobile/Tablet: stacked with video first */}
				<Row className="align-items-stretch g-4 mb-3">
					{/* VIDEO — first on mobile, left on desktop */}
					<Col xs={12} lg={6} className="order-1">
						<div className="ratio ratio-16x9">
							<iframe
								src="https://player.vimeo.com/video/826506355?h=51c8710145&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
								allow="autoplay; fullscreen; picture-in-picture"
								title="How To Setup Your Camera For Analyzing Your Swing"
							/>
						</div>
					</Col>

					{/* BULLETS — second on mobile, right on desktop */}
					<Col xs={12} lg={6} className="order-2">
						<Card className="h-100 p-3">
							<h6 className="mb-2">Before you upload</h6>
							<ul className="mb-0">
								<li>Record a swing face on and down the line</li>
								<li>Hitting a ball is preferred but <b>NOT</b> required</li>
								<li>Keep it simple, short and under 30 seconds.</li>
							</ul>

							{/* Optional helper/counters */}
							<div className="mt-3">
								<small className="text-muted d-block">
									You may upload up to <b>two videos</b> and <b>one report</b> (PDF/XLS/XLSX/CSV).
								</small>
								<div className="d-flex gap-2 flex-wrap mt-2">
									<span className="badge bg-primary">Videos selected: {videosSelected}/2</span>
									<span className="badge bg-secondary">Report selected: {reportsSelected}/1</span>
								</div>
							</div>
						</Card>
					</Col>
				</Row>

				{/* Form inputs below */}
				<label>Current Handicap (0-40) - update if changed</label>
				<input
					type="number"
					id="handicap"
					min={0}
					max={40}
					required
					name="handicap"
					defaultValue={user.handicap || 0}
					className="form-control mb-4"
				/>

				<label>Notes for Instructor (optional)</label>
				<textarea
					className="form-control mb-4"
					name="notes"
					id="notes"
					rows={3}
					placeholder="Give us some notes about the swings you’re submitting."
				/>

				{/* Dropzone area */}
				<div ref={ref} className={className} style={style}>
					{previews}
					{files.length < maxFiles && input}
					{files.length > 0 && submitButton}
				</div>
			</Container>
		</>
	);
};



const VideoUpload = ( { redirectTo, consult = 0 , instructorId = 0, origin = null}) => {
	const { setUser } = useAuth();
	const navigate = useNavigate();
	const [show, setShow] = useState(false);

	// robust counters (don’t rely on setState timings)
	const counted = useRef(new WeakSet());     // files we've counted
	const videoCount = useRef(0);
	const reportCount = useRef(0);

	const incFor = (file) => {
		if (counted.current.has(file)) return true; // already counted
		if (isVideoFile(file)) {
			if (videoCount.current >= 2) return false;
			videoCount.current += 1;
			counted.current.add(file);
			return true;
		}
		if (isReportFile(file)) {
			if (reportCount.current >= 1) return false;
			reportCount.current += 1;
			counted.current.add(file);
			return true;
		}
		return false; // unsupported
	};

	const decFor = (file) => {
		if (!counted.current.has(file)) return;
		if (isVideoFile(file)) {
			videoCount.current = Math.max(0, videoCount.current - 1);
		} else if (isReportFile(file)) {
			reportCount.current = Math.max(0, reportCount.current - 1);
		}
		counted.current.delete(file);
	};

	const handleClose = () => {
		setShow(false);
		if (redirectTo) {
			return navigate(redirectTo, { replace: true });
		}
		return navigate("/dna-profile", { replace: true });
	}
	const handleShow = () => setShow(true);

	const getUploadParams = async ({ meta: { name, type } }) => {
		const response = await API.post("reviews/upload-videos", { name, type });
		return { fields: response.data.fields, meta: { fileUrl: response.data.fileUrl }, url: response.data.uploadUrl }
	}

	// Enforce 2 videos + 1 report immediately on first entry
	const handleChangeStatus = (file, status) => {
		// Treat these as “entering” statuses (different forks fire different ones)
		if (status === 'added' || status === 'preparing' || status === 'ready') {
			// reject unsupported types first
			if (!isVideoFile(file) && !isReportFile(file)) {
				toast.error('Unsupported file type. Allowed: MP4/AVI/MOV/WEBM (videos) or a single PDF/XLS/XLSX/CSV (report).', { position: toast.POSITION.TOP_RIGHT });
				file.remove();
				return;
			}
			// enforce per-type caps
			const ok = incFor(file);
			if (!ok) {
				if (isVideoFile(file)) {
					toast.error('You can upload at most 2 videos.', { position: toast.POSITION.TOP_RIGHT });
				} else {
					toast.error('You can upload only 1 report file.', { position: toast.POSITION.TOP_RIGHT });
				}
				file.remove();
				return;
			}
		}

		// Clean up counters if file leaves or errors
		if (
			status === 'removed' ||
			status === 'aborted' ||
			status === 'rejected_file_type' ||
			status === 'error_file_size' ||
			status === 'error_upload'
		) {
			decFor(file);
		}
	};

	const handleSubmit = async (files, allFiles) => {
		const elem = document.getElementById('notes');
		const elemHandicap = document.getElementById('handicap');

		// Final guard
		const vids = files.filter(f => isVideoFile(f));
		const reports = files.filter(f => isReportFile(f));
		if (vids.length > 2) {
			notify('Please keep it to at most 2 videos.');
			return;
		}
		if (reports.length > 1) {
			notify('Please include only 1 report file.');
			return;
		}

		try {
			await API.post("reviews", { notes: elem.value, handicap: elemHandicap.value, files, consult, instructorId, origin });
			if (origin === 'free-swing-review') {
				trackEvent('generate_lead', { lead_type: 'free-swing-review' });
			}
			allFiles.forEach(f => f.remove());
			elem.value = '';

			// reset counters
			counted.current = new WeakSet();
			videoCount.current = 0;
			reportCount.current = 0;

			handleShow();
		} catch (e) {
			elem.value = '';
			allFiles.forEach(f => f.remove());
			counted.current = new WeakSet();
			videoCount.current = 0;
			reportCount.current = 0;
			notify(e?.response?.data?.message || 'Upload failed');
		}
	}

	return (
		<>
			<Dropzone
				getUploadParams={getUploadParams}
				onChangeStatus={handleChangeStatus}
				LayoutComponent={NoDropzoneLayout}
				onSubmit={handleSubmit}
				//inputComponent={Input}
				inputContent="Upload Files (2 Videos/1 Report)" // left as-is per your note
				//styles={{ dropzone: { minHeight: 200, maxHeight: 250 } }}
				accept=".pdf,.xls,.xlsx,.csv,.mp4,.avi,.mov,.webm"
				autoUpload={true}
				maxFiles={3} // total: 2 videos + 1 report
			/>
			<Modal
				show={show}
				onHide={handleClose}
				backdrop="static"
				keyboard={false}
			>
				<Modal.Header>
					<Modal.Title>You're all set!</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					Your virtual lesson has been submitted for review. We’ll get back to you as quickly as possible.<br/><br/>
					<b>Expected Response Time (If not sooner)</b><br/><br/>
					<b>Premier:</b> 48-72 Business Hours<br/><br/>
					<b>Premier+:</b> 36-48 Business Hours<br/><br/>
					<b>PRO:M</b> 24 hours or less
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={handleClose}>Understood</Button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default VideoUpload;
